import { createContext, useContext } from 'react';
import { createHttpClient, type ClutchClient } from './api';

/** Safe storage access (private mode / blocked storage must not break the app). */
export const store = {
  get(key: string, kind: 'local' | 'session' = 'local'): string | null {
    try {
      return (kind === 'local' ? localStorage : sessionStorage).getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string, kind: 'local' | 'session' = 'local'): void {
    try {
      (kind === 'local' ? localStorage : sessionStorage).setItem(key, value);
    } catch {
      /* ignore */
    }
  },
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

const DRIVER_KEY = 'clutch.driver';

/**
 * `?driver=fixture` (dev builds only) switches to the local fixture driver for this tab.
 * `&game=<gameId>` picks a committed fixture (default 0022500001); `&speed=` sets pacing;
 * `&model=1` runs the real committed win-probability model; `&media=<youtubeId>` serves an
 * official video for every game (to build the highlights UI without the server).
 */
export function fixtureMode(): { on: boolean; speed: number; game?: string; model?: boolean; media?: string } {
  if (!import.meta.env.DEV) return { on: false, speed: 1 };
  const q = new URLSearchParams(location.search);
  const d = q.get('driver');
  if (d === 'fixture')
    store.set(
      DRIVER_KEY,
      JSON.stringify({
        speed: Number(q.get('speed')) || 1,
        game: q.get('game') ?? undefined,
        model: q.get('model') === '1' || undefined,
        media: q.get('media') ?? undefined,
      }),
      'session',
    );
  if (d === 'server') store.set(DRIVER_KEY, '', 'session');
  const saved = store.get(DRIVER_KEY, 'session');
  if (!saved) return { on: false, speed: 1 };
  try {
    const v = JSON.parse(saved) as { speed?: number; game?: string; model?: boolean; media?: string };
    return {
      on: true,
      speed: v.speed && v.speed > 0 ? v.speed : 1,
      ...(v.game ? { game: v.game } : {}),
      ...(v.model ? { model: true } : {}),
      ...(v.media && /^[\w-]{6,20}$/.test(v.media) ? { media: v.media } : {}),
    };
  } catch {
    return { on: false, speed: 1 };
  }
}

export async function loadClient(): Promise<ClutchClient> {
  const fx = fixtureMode();
  if (import.meta.env.DEV && fx.on) {
    const { createFixtureClient } = await import('./dev/fixtureDriver');
    const client = await createFixtureClient({
      speed: fx.speed,
      ...(fx.game ? { gameId: fx.game } : {}),
      ...(fx.model ? { model: true } : {}),
      ...(fx.media ? { mediaVideoId: fx.media } : {}),
    });
    (window as unknown as { __clutchDriver?: unknown }).__clutchDriver = client; // e2e/dev hook
    return client;
  }
  return createHttpClient('/api');
}

export const ClientContext = createContext<ClutchClient | null>(null);
export function useClient(): ClutchClient {
  const c = useContext(ClientContext);
  if (!c) throw new Error('ClientContext missing');
  return c;
}
